import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { adminApi, type AdminUser, type UserRole } from '../../services/adminApi';
import { useAuthStore } from '../../stores/authStore';

const ROLES: UserRole[] = ['user', 'admin', 'advertiser'];
const PAGE_SIZE = 25;

/**
 * Admin user-management index page (GitHub #12 area 1).
 *
 * Lists users with an email search box, lets an admin change a user's role and
 * suspend / reactivate accounts. Auth is enforced upstream by AdminLayout
 * (redirects non-admins); the server independently re-checks admin role and
 * blocks self-demotion / self-suspension. The UI mirrors those guards by
 * disabling self-targeting destructive controls.
 *
 * Privacy: the list shows only email, role, account status and created date —
 * never password hashes or other PII (the API also strips them).
 */
export function UsersListPage() {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.claims?.sub);

  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Ban dialog: the user being banned and the required reason.
  const [banTarget, setBanTarget] = useState<AdminUser | null>(null);
  const [banReason, setBanReason] = useState('');

  const query = useQuery({
    queryKey: ['admin', 'users', { search, page }],
    queryFn: () => adminApi.listUsers({ search: search || undefined, page, limit: PAGE_SIZE }),
  });

  const mutation = useMutation({
    mutationFn: (vars: { id: string; patch: { role?: UserRole; isActive?: boolean } }) =>
      adminApi.patchUser(vars.id, vars.patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const banMutation = useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      vars.reason !== undefined
        ? adminApi.banUser(vars.id, vars.reason)
        : adminApi.unbanUser(vars.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  // Email verification (#209): manually verify or re-send the link.
  const verifyMutation = useMutation({
    mutationFn: (vars: { id: string; action: 'verify' | 'resend' }) =>
      vars.action === 'verify'
        ? adminApi.verifyUser(vars.id)
        : adminApi.resendVerification(vars.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  function onSearchSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  }

  function onRoleChange(user: AdminUser, event: SelectChangeEvent<string>) {
    const role = event.target.value as UserRole;
    if (role === user.role) return;
    mutation.mutate({ id: user.id, patch: { role } });
  }

  function onToggleActive(user: AdminUser) {
    mutation.mutate({ id: user.id, patch: { isActive: !user.isActive } });
  }

  function openBanDialog(user: AdminUser) {
    setBanReason('');
    setBanTarget(user);
  }

  function confirmBan() {
    if (!banTarget || banReason.trim().length < 3) return;
    banMutation.mutate(
      { id: banTarget.id, reason: banReason.trim() },
      { onSuccess: () => setBanTarget(null) }
    );
  }

  function onUnban(user: AdminUser) {
    banMutation.mutate({ id: user.id });
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        User management
      </Typography>

      <Paper
        component="form"
        onSubmit={onSearchSubmit}
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'flex-end' }}
        elevation={0}
        variant="outlined"
      >
        <TextField
          id="user-search"
          label="Search by email"
          size="small"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Paper>

      {(mutation.isError || banMutation.isError || verifyMutation.isError) && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Update failed:{' '}
          {((mutation.error || banMutation.error || verifyMutation.error) as Error).message}
        </Alert>
      )}

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading users" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load users: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Users">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No users match the current search.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.users.map((user) => {
                    const isSelf = user.id === currentUserId;
                    return (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <MuiLink component={RouterLink} to={`/admin/users/${user.id}`}>
                            {user.email}
                          </MuiLink>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel id={`role-label-${user.id}`}>Role</InputLabel>
                            <Select
                              labelId={`role-label-${user.id}`}
                              id={`role-${user.id}`}
                              value={user.role}
                              label="Role"
                              // Self-demotion is blocked server-side; mirror it here.
                              disabled={isSelf || mutation.isPending}
                              onChange={(e) => onRoleChange(user, e)}
                              inputProps={{
                                'aria-label': `Role for ${user.email}`,
                              }}
                            >
                              {ROLES.map((r) => (
                                <MenuItem key={r} value={r}>
                                  {r}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {user.bannedAt ? (
                              <Chip
                                label="Banned"
                                color="error"
                                size="small"
                                title={user.banReason ?? undefined}
                              />
                            ) : (
                              <Chip
                                label={user.isActive ? 'Active' : 'Suspended'}
                                color={user.isActive ? 'success' : 'default'}
                                size="small"
                              />
                            )}
                            <Chip
                              label={user.emailVerifiedAt ? 'Verified' : 'Unverified'}
                              color={user.emailVerifiedAt ? 'success' : 'warning'}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>{user.createdAt ?? '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button
                              size="small"
                              variant="outlined"
                              color={user.isActive ? 'warning' : 'success'}
                              // Self-suspension is blocked server-side; mirror it here.
                              disabled={isSelf || mutation.isPending}
                              onClick={() => onToggleActive(user)}
                              aria-label={`${user.isActive ? 'Suspend' : 'Activate'} ${user.email}`}
                            >
                              {user.isActive ? 'Suspend' : 'Activate'}
                            </Button>
                            {!user.emailVerifiedAt && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={verifyMutation.isPending}
                                onClick={() =>
                                  verifyMutation.mutate({ id: user.id, action: 'verify' })
                                }
                                aria-label={`Verify email for ${user.email}`}
                              >
                                Verify
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={verifyMutation.isPending}
                              onClick={() =>
                                verifyMutation.mutate({ id: user.id, action: 'resend' })
                              }
                              aria-label={`Resend verification email to ${user.email}`}
                            >
                              Resend
                            </Button>
                            {user.bannedAt ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={isSelf || banMutation.isPending}
                                onClick={() => onUnban(user)}
                                aria-label={`Unban ${user.email}`}
                              >
                                Unban
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                // Self-ban is blocked server-side; mirror it here.
                                disabled={isSelf || banMutation.isPending}
                                onClick={() => openBanDialog(user)}
                                aria-label={`Ban ${user.email}`}
                              >
                                Ban
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Users pagination"
            />
          </Box>
        </>
      )}

      <Dialog
        open={banTarget !== null}
        onClose={() => setBanTarget(null)}
        aria-labelledby="ban-title"
      >
        <DialogTitle id="ban-title">Ban {banTarget?.email}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Banning blocks this account from signing in. Enter a reason — it&apos;s recorded in the
            audit log and shown in the user list.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            inputProps={{ 'aria-label': 'Ban reason' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={banReason.trim().length < 3 || banMutation.isPending}
            onClick={confirmBan}
          >
            Ban user
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UsersListPage;
